import { DeterministicView } from '@bingads-webui/deterministic-view';
import { keyCode } from '@bingads-webui/keycode';

import '@bingads-webui/kendo/kendo.datepicker';
import { validateDateRange } from '@bingads-webui-universal/primitive-utilities';

import template from './date-picker-template.pug';

/**
 * dateChanged event - When date is changed, it fires with the new date object.
 *
 * @event DatePicker#dateChanged
 * @type {Date}
 */

/**
 * Date picker control used to select dates.
 * @fires DatePicker#dateChanged
 */
export class DatePicker extends DeterministicView {
  /**
   * initialize DatePicker view.
   * It defaults to short date format from passed in culture if not overridden.
   * @param {Object} options - options object.
   * @param {Date} [options.minDate] - The minimum date that can be selected.
   * @param {Date} [options.maxDate] - The maximum date that can be selected.
   * @param {Date} [options.selectedDate] - The date to pre-select in the datepicker.
   * @param {string} [options.ariaLabel] - The aria-label of datepicker.
   * If not provided, datepicker will not have a default selection.
   * @param {string} [options.navigateToFutureAriaLabel] - the aria label used on the navigate to future button
   * @param {string} [options.navigateToPastAriaLabel] - the aria label used on the navigate to past button
   * @throws Will throw if passed in culture has not been loaded.
   * @throws Will throw if passed in date range is not valid.
   * @returns {undefined}
   */
  initialize({
    minDate, maxDate, selectedDate, ariaLabel, navigateToFutureAriaLabel, navigateToPastAriaLabel,
  }) {
    const dateRangeErrors = validateDateRange(minDate, maxDate, selectedDate);
    if (dateRangeErrors.length) {
      throw new Error(dateRangeErrors.join(','));
    }

    this.model.set({
      minDate, maxDate, selectedDate, ariaLabel, navigateToFutureAriaLabel, navigateToPastAriaLabel,
    });
  }

  /**
   * Returns event handler callback for clicking on date filter container.
   * @returns {Object} - event handlers mapping.
   */
  get events() {
    return {
      'click input.date-picker-component': () => this.datePicker.open(),
      'keydown input.date-picker-component': this.handleKeyDown,
    };
  }

  /**
   * Returns the selected date.
   * @returns {Date} - Date object or null if no date selected.
   */
  getDate() {
    return this.getState().selectedDate || null;
  }

  /**
   * Returns the options for kendo date picker
   * @returns {Object} - options
   */
  getKendoOptions() {
    const { minDate, maxDate, selectedDate } = this.model.attributes;

    const options = {
      value: selectedDate,
      animation: false,
      footer: false,
      change: (event) => {
        const newDate = event.sender.value();
        if (newDate) {
          this.model.set({ selectedDate: newDate }, { silent: true });
          this.trigger('dateChanged', this.getDate());
        } else {
          // For invalid values, kendo might not update input box
          // so have to do it manually
          event.sender.value(this.getDate());
        }
      },
      open: () => {
        this.datePicker.dateView.div.click((event) => {
          event.stopPropagation();
        });
        // Fix Bug 3084144: Bad gradient effect of the date picker
        const { calendar } = this.datePicker.dateView;
        if (calendar && calendar.setOptions) {
          calendar.setOptions({ animation: false });
        }

        if (this.model.attributes.navigateToPastAriaLabel) {
          // eslint-disable-next-line no-underscore-dangle
          calendar._prevArrow.attr('aria-label', this.model.attributes.navigateToPastAriaLabel);
        }
        if (this.model.attributes.navigateToFutureAriaLabel) {
          // eslint-disable-next-line no-underscore-dangle
          calendar._nextArrow.attr('aria-label', this.model.attributes.navigateToFutureAriaLabel);
        }
      },
      month: {
        empty: '<span class="k-state-disabled">#= data.value #</span>',
      },
    };

    // set min and max only if they are not null
    // otherwise may get error: Uncaught TypeError: Cannot read property 'getMonth' of null
    if (minDate) {
      options.min = minDate;
    }

    if (maxDate) {
      options.max = maxDate;
    }

    return options;
  }

  /**
   * Method to initialize and render the underlying date-picker.
   * @private
   * @returns {undefined}
   */
  renderTrunk() {
    this.$el.html(template({ ariaLabel: this.model.attributes.ariaLabel }));
    this.datePickerElement = this.$('.date-picker-component');

    this.datePickerElement.kendoDatePicker(this.getKendoOptions());

    this.datePickerElement.removeAttr('aria-readonly');

    this.$('.k-datepicker .k-select').hide();

    if (this.datePicker) {
      this.datePicker.destroy();
    }
    this.datePicker = this.datePickerElement.data('kendoDatePicker');
  }

  /**
   * Destroy the underlying date-picker object and remove all references to it.
   * @private
   * @returns {undefined}
   */
  remove() {
    if (this.datePicker) {
      this.datePicker.destroy();
    }

    this.datePicker = null;
    return super.remove();
  }

  /**
 * Handles keydown event for the date picker.
 * @private
 * @param {KeyboardEvent} event - the event data
 * @returns {undefined}
 */
  handleKeyDown(event) {
    if ((event.keyCode === keyCode.Enter) || (event.keyCode === keyCode.Space)) {
      event.preventDefault();
      event.stopPropagation();
      this.datePicker.open();
    }
  }
}
